/**
 * Générateur — Aborder les questions relatives au hasard à partir de problèmes simples.
 * Attendu ID : 977 | Niveau : 5ème | Domaine : Probabilités (descripteur 132)
 *
 * Version conforme à la spécification 977 (2026-09-21).
 * Supprimée : la carte « Deux pièces : PILE-PILE ou FACE-FACE ? » (4 formats).
 * Elle décrivait une expérience à DEUX épreuves, ce que la spec exclut sans
 * réserve (« aucune expérience à deux épreuves, ni successives ni simultanées :
 * le programme situe ce cas au niveau suivant »), et ses événements « au moins
 * un PILE » et « deux résultats identiques » sont des réunions, elles aussi
 * exclues. Son contenu utile — la fluctuation d'échantillonnage et la
 * stabilisation des fréquences — est repris par la carte « Le dé est-il
 * truqué ? », construite sur une épreuve UNIQUE répétée.
 * Ajoutées : « L'urne de la classe » (niveau 1, situation symétrique, où
 * l'équiprobabilité se justifie), « Le dé est-il truqué ? » (niveau 3, deux
 * tailles de série simulées) et « La situation et son modèle » (niveau 4, la
 * distinction que le préambule du programme exige).
 *
 * Plan de cartes :
 *   « L'urne de la classe » (niveau 1) — huit boules numérotées et
 *     indiscernables au toucher, réparties en trois couleurs d'effectifs
 *     DIFFÉRENTS : les huit numéros sont équiprobables par symétrie, les trois
 *     couleurs ne le sont pas.
 *   « Le dé aux faces peintes » (niveau 2) — trois couleurs sur trois, deux et
 *     une faces : l'équiprobabilité des couleurs ne tient pas, et les issues se
 *     classent de la moins à la plus probable SANS aucun calcul.
 *   « Qui a le plus de chances ? » (niveau 2) — trois sacs de billes de
 *     compositions différentes : c'est la proportion qui décide, pas le nombre
 *     absolu de billes rouges.
 *   « Le dé est-il truqué ? » (niveau 3) — une même épreuve répétée 50 puis
 *     2000 fois, effectifs produits par une SIMULATION réelle : la fréquence
 *     observée de la grande série est plus proche du modèle que celle de la
 *     petite, vérifié à la génération.
 *   « La situation et son modèle » (niveau 4) — deux affirmations sur une même
 *     expérience, dont l'une confond la situation réelle et le modèle ; et une
 *     situation où le hasard n'intervient pas, à reconnaître comme telle.
 *   « L'échelle de probabilité » (niveau 1) — relier, classer, QCM, glisser :
 *     positionner des événements d'une SEULE épreuve de 0 à 1.
 *
 * Spécification appliquée :
 *   Bornes : 2 à 10 issues par expérience ; UNE seule épreuve (variable fermée
 *     à 1) ; séries simulées de 50 et 2000 répétitions ; fréquence observée
 *     arrondie au centième ; écart entre la fréquence observée et la
 *     probabilité du modèle au plus 0,2, et franchement plus petit pour la
 *     grande série ; 2 à 3 questions par situation.
 *   Exclusions : aucune expérience à deux épreuves ; aucun évènement contraire,
 *     aucune réunion, aucune intersection (les événements « au moins un… »,
 *     « P et F en même temps » ont disparu) ; aucune probabilité posée sans
 *     justification (chaque modèle est posé par symétrie annoncée ou construit
 *     sur un relevé fourni) ; aucun arbre ni tableau de dénombrement ; aucune
 *     situation dépendant d'une compétence.
 *   Erreurs visées → mots-clés (`_meta.params.distracteurs[].code`) :
 *     - exiger que la fréquence observée égale la probabilité → `frequence_egale_a_la_probabilite`
 *     - raisonnement de compensation après une série de résultats → `raisonnement_de_compensation`
 *     - supposer l'équiprobabilité là où elle n'a pas lieu → `equiprobabilite_supposee`
 *     - confondre le nombre d'issues et le nombre d'objets → `issues_confondues_avec_objets`
 *     - confondre la situation réelle et son modèle → `situation_confondue_avec_le_modele`
 *     - croire que hasard implique équiprobabilité → `hasard_implique_equiprobabilite`
 *   Graduation → cartes : 1 « L'urne de la classe » et « L'échelle de
 *     probabilité » ; 2 « Le dé aux faces peintes » et « Qui a le plus de
 *     chances ? » ; 3 « Le dé est-il truqué ? » ; 4 « La situation et son
 *     modèle ».
 *   Non tranché : la spec demande un champ libre de justification à côté de
 *     chaque choix binaire, et un curseur sur l'échelle de probabilité ; aucun
 *     format TanoEd ne les fournit. Les justifications sont portées par les
 *     choix eux-mêmes, et l'échelle est servie par relier, classer et glisser.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeRelier, makeClasser, makeOrdonner,
    filtrerDistracteurs,
} from '../../../utils';
import { imgElement } from '../statistiques/svgStats';
import { svgEchelle, svgUrne } from './svgProbas';

/** Fractions en KaTeX là où l'élève les lit ; les pools gardent « 1/4 » en
 *  texte, car ce sont des clés et des étiquettes à glisser. */
const fr = (s) => {
    const m = String(s).match(/^(\d+)\/(\d+)$/);
    return m ? `$\\dfrac{${m[1]}}{${m[2]}}$` : s;
};

const ATTENDU_ID = 977;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_URNE = 'L\'urne de la classe';
const L_DE = 'Le dé aux faces peintes';
const L_BILLES = 'Qui a le plus de chances ?';
const L_SIMU = 'Le dé est-il truqué ?';
const L_MODELE = 'La situation et son modèle';
const L_ECHELLE = 'L\'échelle de probabilité';

// ─── Helpers numériques (entiers uniquement) ───

/** c/n en écriture décimale FR exacte (n divise 1000), sans zéros finaux. */
const propFr = (c, n) => {
    const mi = (c * 1000) / n;
    const e = Math.floor(mi / 1000);
    const r = mi % 1000;
    return r === 0 ? String(e) : `${e},${String(r).padStart(3, '0').replace(/0+$/, '')}`;
};

/** k vingtièmes → « 0,45 ». */
const vingtiemesFr = (k) => propFr(k, 20);

/** centièmes entiers → « 0,17 » / « 0,2 » / « 1 ». */
const centiemesFr = (c) => {
    if (c % 100 === 0) return String(c / 100);
    const r = String(c % 100).padStart(2, '0').replace(/0$/, '');
    return `${Math.floor(c / 100)},${r}`;
};

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

/** Élisions devant voyelle ou H muet (Inès, Emma, Alice, Héloïse…). */
const VOYELLE = /^[AEIOUYHÀÂÉÈÊËÎÏÔÙÛ]/;
const dElide = (p) => (VOYELLE.test(p) ? `d'${p}` : `de ${p}`);
const queElide = (p) => (VOYELLE.test(p) ? `qu'${p}` : `que ${p}`);

/** Garantit qu'un tableau vrai/faux n'est ni tout vrai ni tout faux. */
const equilibrer = (vs) => {
    const nbVrai = vs.filter(Boolean).length;
    if (nbVrai === 0) vs[0] = true;
    else if (nbVrai === vs.length) vs[0] = false;
    return vs;
};

// ═════════════════════════════════════════════════════════════
// CARTE « L'urne de la classe » (niveau 1 — situation symétrique)
// ═════════════════════════════════════════════════════════════

const COULEURS_URNE = ['rouge', 'bleue', 'verte', 'jaune', 'violette', 'noire'];
const REPARTITIONS_URNE = [[4, 3, 1], [5, 2, 1], [4, 2, 2], [3, 3, 2]];

/** Huit boules NUMÉROTÉES de 1 à 8, indiscernables au toucher, réparties en
 *  trois couleurs d'effectifs non tous égaux : les huit numéros sont
 *  équiprobables, les trois couleurs ne le sont pas. */
const tirerUrne = () => {
    const parts = randomChoice(REPARTITIONS_URNE);
    const couleurs = shuffle([...COULEURS_URNE]).slice(0, 3);
    const numeros = shuffle([1, 2, 3, 4, 5, 6, 7, 8]);
    const boules = [];
    let k = 0;
    parts.forEach((n, i) => {
        for (let j = 0; j < n; j++) { boules.push({ couleur: couleurs[i], numero: numeros[k] }); k += 1; }
    });
    const iPlus = parts.indexOf(Math.max(...parts));
    const iMoins = parts.indexOf(Math.min(...parts));
    return { parts, couleurs, boules: shuffle(boules), iPlus, iMoins };
};

const introUrne = (d) => '<p>Une urne contient huit boules numérotées de 1 à 8, toutes de la même '
    + 'taille et indiscernables au toucher. Elles portent trois couleurs : '
    + `${d.parts.map((n, i) => `${n} ${d.couleurs[i]}${n > 1 ? 's' : ''}`).join(', ')}. `
    + 'On tire une boule au hasard et on note son numéro.</p>';

const elUrne = (d) => imgElement(svgUrne(d.boules));

const paramsUrne = (d, extra = {}) => ({
    niveau: 1,
    type_de_situation: 'situation symétrique',
    nombre_d_issues_de_l_experience: 8,
    nombre_d_epreuves: 1,
    nombre_de_questions_par_situation: 2,
    nombre_d_objets: 8,
    repartition_des_couleurs: d.parts,
    ...extra,
});

const genUrneQcm = () => {
    const d = tirerUrne();
    const bonne = 'Parce que les huit boules sont de même taille et indiscernables au toucher : aucune n\'est plus facile à prendre';
    const dist = [
        { code: 'hasard_implique_equiprobabilite', valeur: 'Parce qu\'on ne connaît pas le résultat à l\'avance : c\'est toujours le cas quand il y a du hasard' },
        { code: 'issues_confondues_avec_objets', valeur: 'Parce qu\'il y a autant de numéros possibles que de couleurs dans l\'urne' },
        { code: 'equiprobabilite_supposee', valeur: 'Parce que chaque couleur est présente le même nombre de fois dans l\'urne' },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `${introUrne(d)}<p>Les huit numéros ont la même probabilité d'être tirés. Pourquoi ?</p>`,
        opts(L_URNE));
    payload.elements_additionnels = elUrne(d);
    payload._meta.params = paramsUrne(d, { distracteurs: dist });
    return payload;
};

const genUrneVF = () => {
    const d = tirerUrne();
    const vs = equilibrer([0, 1, 2, 3].map(() => randInt(0, 1) === 0));
    const codes = [];
    if (!vs[0]) codes.push('issues_confondues_avec_objets');
    if (!vs[2]) codes.push('equiprobabilite_supposee');
    if (!vs[3]) codes.push('hasard_implique_equiprobabilite');
    const lignes = [
        [`En notant le numéro de la boule tirée, ce tirage a ${vs[0] ? 'huit' : 'trois'} issues possibles.`, vs[0] ? 0 : 1],
        [`Les huit numéros ${vs[1] ? 'ont' : 'n\'ont pas'} la même probabilité d'être tirés.`, vs[1] ? 0 : 1],
        [vs[2]
            ? `La couleur ${d.couleurs[d.iPlus]} a plus de chances de sortir que la couleur ${d.couleurs[d.iMoins]}.`
            : 'Les trois couleurs ont toutes la même probabilité de sortir.', vs[2] ? 0 : 1],
        [vs[3]
            ? 'Une situation due au hasard n\'a pas forcément des issues de même probabilité.'
            : 'Dès qu\'une situation est due au hasard, toutes ses issues ont la même probabilité.', vs[3] ? 0 : 1],
    ];
    const melange = shuffle(lignes);
    const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
        `${introUrne(d)}<p>Vrai ou faux ?</p>`,
        { ...opts(L_URNE), elements_additionnels: elUrne(d) });
    payload._meta.params = paramsUrne(d, { distracteurs: codes.map((code) => ({ code })) });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Le dé aux faces peintes » (niveau 2 — non symétrique)
// ═════════════════════════════════════════════════════════════

const COULEURS_DE = ['bleue', 'rouge', 'jaune', 'verte', 'noire', 'blanche', 'violette', 'rose'];
const EN_LETTRES = { 1: 'une', 2: 'deux', 3: 'trois' };

/** Trois couleurs sur 3, 2 et 1 faces : l'ordre des probabilités est STRICT,
 *  donc le classement de la moins à la plus probable a une seule solution. */
const tirerDe = () => {
    const couleurs = shuffle([...COULEURS_DE]).slice(0, 3);
    return { couleurs, faces: [3, 2, 1] };   // couleurs[i] est sur faces[i] faces
};

const descDe = (t) =>
    `Un dé équilibré à six faces est peint : ${t.couleurs
        .map((c, i) => `${EN_LETTRES[t.faces[i]]} face${t.faces[i] > 1 ? 's' : ''} ${c}${t.faces[i] > 1 ? 's' : ''}`)
        .join(', ')}. On lance ce dé une fois et on note la couleur de la face du dessus.`;

const paramsDe = (t, extra = {}) => ({
    niveau: 2,
    type_de_situation: 'situation non symétrique observable',
    nombre_d_issues_de_l_experience: 3,
    nombre_d_epreuves: 1,
    nombre_de_questions_par_situation: 3,
    nombre_d_objets: 6,
    faces_par_couleur: t.faces,
    ...extra,
});

const genDeOrdonner = () => {
    const t = tirerDe();
    // De la MOINS probable à la PLUS probable : une face, deux faces, trois faces.
    const ordre = [t.couleurs[2], t.couleurs[1], t.couleurs[0]];
    const payload = makeOrdonner(ordre,
        `<p>${descDe(t)}</p><p>Range les trois couleurs de la MOINS probable à la PLUS probable. `
        + 'Aucun calcul n\'est nécessaire : il suffit de compter les faces.</p>',
        opts(L_DE));
    payload._meta.params = paramsDe(t, { erreurVisee: 'equiprobabilite_supposee' });
    return payload;
};

const genDeQcm = () => {
    const t = tirerDe();
    if (randInt(0, 1) === 0) {
        const bonne = `Non : ${t.couleurs[0]} est sur trois faces, ${t.couleurs[1]} sur deux et ${t.couleurs[2]} sur une seule`;
        const dist = [
            { code: 'equiprobabilite_supposee', valeur: 'Oui : le dé est équilibré, donc toutes les couleurs ont la même probabilité' },
            { code: 'issues_confondues_avec_objets', valeur: 'Oui : il y a trois couleurs, donc chacune a une chance sur trois' },
            { code: 'hasard_implique_equiprobabilite', valeur: 'Oui : on ne connaît pas le résultat à l\'avance, donc les issues ont la même chance' },
        ];
        const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
            `<p>${descDe(t)}</p><p>Les trois couleurs ont-elles la même probabilité d'être obtenues ?</p>`,
            opts(L_DE));
        payload._meta.params = paramsDe(t, { distracteurs: dist });
        return payload;
    }
    const bonne = cap(t.couleurs[0]);
    const dist = [
        { code: 'equiprobabilite_supposee', valeur: 'Les trois couleurs ont la même probabilité' },
        { code: 'equiprobabilite_supposee', valeur: cap(t.couleurs[2]) },
        { code: 'equiprobabilite_supposee', valeur: cap(t.couleurs[1]) },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `<p>${descDe(t)}</p><p>Quelle couleur a la plus grande probabilité d'être obtenue ?</p>`,
        opts(L_DE));
    payload._meta.params = paramsDe(t, { distracteurs: dist });
    return payload;
};

const genDeVF = () => {
    const t = tirerDe();
    const vs = equilibrer([0, 1, 2, 3].map(() => randInt(0, 1) === 0));
    const codes = [];
    if (!vs[1]) codes.push('equiprobabilite_supposee');
    if (!vs[2]) codes.push('issues_confondues_avec_objets');
    if (!vs[3]) codes.push('equiprobabilite_supposee');
    const lignes = [
        [`Chaque face du dé ${vs[0] ? 'a' : 'n\'a pas'} la même probabilité d'apparaître.`, vs[0] ? 0 : 1],
        [vs[1]
            ? 'Les trois couleurs n\'ont pas toutes la même probabilité d\'apparaître.'
            : 'Les trois couleurs ont toutes la même probabilité d\'apparaître.', vs[1] ? 0 : 1],
        [vs[2]
            ? 'Il y a trois couleurs possibles, mais elles ne se partagent pas les six faces à parts égales.'
            : 'Il y a trois couleurs possibles, donc chacune a une chance sur trois.', vs[2] ? 0 : 1],
        [`La couleur ${vs[3] ? t.couleurs[0] : t.couleurs[2]} est plus probable que la couleur ${vs[3] ? t.couleurs[2] : t.couleurs[0]}.`, vs[3] ? 0 : 1],
    ];
    const melange = shuffle(lignes);
    const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
        `<p>${descDe(t)}</p><p>Vrai ou faux ?</p>`, opts(L_DE));
    payload._meta.params = paramsDe(t, { distracteurs: codes.map((code) => ({ code })) });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Qui a le plus de chances ? » (niveau 2 — urnes déséquilibrées)
// ═════════════════════════════════════════════════════════════

const TRIOS = [
    ['Sarah', 'Capucine', 'Héloïse'],
    ['Inès', 'Jeanne', 'Louna'],
    ['Lina', 'Zoé', 'Emma'],
    ['Alice', 'Rose', 'Camille'],
];

/** Proportions en vingtièmes DISTINCTS (grille de 0,05 → gagnante unique). */
const tirerBilles = () => {
    const prenoms = shuffle(randomChoice(TRIOS));
    const ks = shuffle([4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14]).slice(0, 3);
    const piege = randInt(0, 1) === 0;
    let totaux;
    if (piege) {
        // La plus grande proportion sur le plus PETIT sac : celle qui gagne n'a
        // PAS le plus de billes rouges en absolu.
        const pool = randomChoice([[20, 100, 200], [20, 60, 200], [40, 100, 200]]);
        const parKdesc = [0, 1, 2].sort((a, b) => ks[b] - ks[a]);
        totaux = [];
        parKdesc.forEach((idx, rang) => { totaux[idx] = pool[rang]; });
    } else {
        totaux = shuffle([20, 40, 100, 200]).slice(0, 3);
    }
    const rouges = ks.map((k, i) => (k * totaux[i]) / 20);
    const noires = totaux.map((t, i) => t - rouges[i]);
    const iGagnante = ks.indexOf(Math.max(...ks));
    return { prenoms, ks, totaux, rouges, noires, iGagnante };
};

const enonceBilles = (b) =>
    '<p>Trois amies ont chacune un sac de billes : '
    + b.prenoms.map((p, i) => `${p} a ${b.rouges[i]} billes rouges et ${b.noires[i]} billes noires`).join(' ; ')
    + '. Chacune tire au hasard une bille de son sac.</p>';

const paramsBilles = (b, extra = {}) => ({
    niveau: 2,
    type_de_situation: 'situation non symétrique observable',
    nombre_d_issues_de_l_experience: 2,
    nombre_d_epreuves: 1,
    nombre_de_questions_par_situation: 3,
    nombre_d_objets: b.totaux,
    ...extra,
});

const genBillesQcm = () => {
    const b = tirerBilles();
    const variante = randInt(0, 2);
    if (variante === 0) {
        const bonne = b.prenoms[b.iGagnante];
        const dist = [
            { code: 'equiprobabilite_supposee', valeur: 'Les trois ont la même probabilité' },
            ...b.prenoms.filter((p) => p !== bonne).map((p) => ({ code: 'issues_confondues_avec_objets', valeur: p })),
        ];
        const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
            `${enonceBilles(b)}<p>Qui a la plus grande probabilité de tirer une bille rouge ?</p>`,
            opts(L_BILLES));
        payload._meta.params = paramsBilles(b, { distracteurs: dist });
        return payload;
    }
    if (variante === 1) {
        const bonne = 'On compare les proportions de billes rouges : le nombre de rouges divisé par le nombre total de billes du sac';
        const dist = [
            { code: 'issues_confondues_avec_objets', valeur: 'On compare seulement les nombres de billes rouges' },
            { code: 'issues_confondues_avec_objets', valeur: 'On compare seulement les nombres de billes noires' },
            { code: 'issues_confondues_avec_objets', valeur: 'Celle qui a le plus de billes en tout a forcément le plus de chances' },
        ];
        const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
            `${enonceBilles(b)}<p>Comment comparer leurs chances de tirer une bille rouge ?</p>`,
            opts(L_BILLES));
        payload._meta.params = paramsBilles(b, { distracteurs: dist });
        return payload;
    }
    // Le piège : plus de rouges en absolu, mais proportion plus faible.
    const trouverPaire = (bb) => {
        for (let x = 0; x < 3; x++) {
            for (let y = 0; y < 3; y++) {
                if (bb.rouges[x] > bb.rouges[y] && bb.ks[x] < bb.ks[y]) return [x, y];
            }
        }
        return null;
    };
    let bp = b;
    let paire = trouverPaire(bp);
    for (let essai = 0; essai < 30 && !paire; essai++) {
        bp = tirerBilles();
        paire = trouverPaire(bp);
    }
    if (!paire) {
        // Repli déterministe : la question « qui gagne » reste posable.
        const bonne = b.prenoms[b.iGagnante];
        const dist = [
            { code: 'equiprobabilite_supposee', valeur: 'Les trois ont la même probabilité' },
            ...b.prenoms.filter((p) => p !== bonne).map((p) => ({ code: 'issues_confondues_avec_objets', valeur: p })),
        ];
        const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
            `${enonceBilles(b)}<p>Qui a la plus grande probabilité de tirer une bille rouge ?</p>`,
            opts(L_BILLES));
        payload._meta.params = paramsBilles(b, { distracteurs: dist });
        return payload;
    }
    const [i, j] = paire;
    const bonne = 'Non : c\'est la proportion de billes rouges dans le sac qui compte, pas leur nombre';
    const dist = [
        { code: 'issues_confondues_avec_objets', valeur: 'Oui : plus un sac contient de billes rouges, plus la probabilité est grande' },
        { code: 'issues_confondues_avec_objets', valeur: 'Oui : elle a aussi plus de billes en tout' },
        { code: 'issues_confondues_avec_objets', valeur: 'Non : c\'est le nombre de billes noires qui décide' },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `${enonceBilles(bp)}<p>${bp.prenoms[i]} a plus de billes rouges ${queElide(bp.prenoms[j])} `
        + `(${bp.rouges[i]} contre ${bp.rouges[j]}). A-t-elle forcément plus de chances de tirer une bille rouge ?</p>`,
        opts(L_BILLES));
    payload._meta.params = paramsBilles(bp, { distracteurs: dist });
    return payload;
};

const genBillesTrous = () => {
    const b = tirerBilles();
    const phrase = b.prenoms.map((p, i) => `Probabilité ${dElide(p)} : {{${vingtiemesFr(b.ks[i])}}}`).join('\n');
    const texte = `${enonceBilles(b)}<p>Calcule, pour chacune, la probabilité de tirer une bille rouge. `
        + 'Écris chaque probabilité en écriture décimale (par exemple 0,45).</p>';
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_BILLES), mode: 'saisie', strict: false });
    payload._meta.params = paramsBilles(b, { erreurVisee: 'issues_confondues_avec_objets', nombre_de_trous: 3 });
    return payload;
};

const genBillesVF = () => {
    const b = tirerBilles();
    const iPerdante = b.ks.indexOf(Math.min(...b.ks));
    const iAutre = [0, 1, 2].find((i) => i !== b.iGagnante && i !== iPerdante);
    const vs = equilibrer([0, 1, 2, 3].map(() => randInt(0, 1) === 0));
    const codes = [];
    if (!vs[0]) codes.push('issues_confondues_avec_objets');
    if (!vs[2]) codes.push('issues_confondues_avec_objets');
    if (!vs[3]) codes.push('equiprobabilite_supposee');
    const lignes = [
        [`${vs[0] ? b.prenoms[b.iGagnante] : b.prenoms[iPerdante]} a la plus grande probabilité de tirer une bille rouge.`, vs[0] ? 0 : 1],
        [`La probabilité ${queElide(b.prenoms[iAutre])} tire une bille rouge est ${vingtiemesFr(vs[1] ? b.ks[iAutre] : b.ks[iPerdante])}.`, vs[1] ? 0 : 1],
        [vs[2]
            ? 'Pour comparer leurs chances, on calcule la proportion de billes rouges de chaque sac.'
            : 'Celle qui a le plus de billes rouges a forcément la plus grande probabilité d\'en tirer une.', vs[2] ? 0 : 1],
        [vs[3]
            ? 'Les trois sacs ne donnent pas la même probabilité de tirer une bille rouge.'
            : 'Les trois sacs donnent la même probabilité de tirer une bille rouge.', vs[3] ? 0 : 1],
    ];
    const melange = shuffle(lignes);
    const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
        `${enonceBilles(b)}<p>Vrai ou faux ?</p>`, opts(L_BILLES));
    payload._meta.params = paramsBilles(b, { distracteurs: codes.map((code) => ({ code })) });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Le dé est-il truqué ? » (niveau 3 — deux tailles de série)
// ═════════════════════════════════════════════════════════════

const DISPOSITIFS_SIM = [
    { nom: 'un dé équilibré à six faces', issues: 6, evt: 'obtenir un 6', chances: 'une chance sur six', pCent: 17 },
    { nom: 'une pièce équilibrée', issues: 2, evt: 'obtenir PILE', chances: 'une chance sur deux', pCent: 50 },
];
const PETITE_SERIE = 50;
const GRANDE_SERIE = 2000;

/** Effectifs produits par une SIMULATION réelle de l'épreuve (jamais par un
 *  tirage direct d'effectifs) ; la grande série doit être franchement plus
 *  proche du modèle que la petite, sinon la série est rejouée. */
const tirerSimulation = () => {
    const dispo = randomChoice(DISPOSITIFS_SIM);
    const simuler = (n) => {
        let c = 0;
        for (let i = 0; i < n; i++) if (randInt(1, dispo.issues) === 1) c += 1;
        return c;
    };
    for (let essai = 0; essai < 60; essai++) {
        const c50 = simuler(PETITE_SERIE);
        const c2000 = simuler(GRANDE_SERIE);
        const f50 = Math.round((c50 * 100) / PETITE_SERIE);          // centièmes entiers
        const f2000 = Math.round((c2000 * 100) / GRANDE_SERIE);
        const e50 = Math.abs(f50 - dispo.pCent);
        const e2000 = Math.abs(f2000 - dispo.pCent);
        if (e50 > 20 || e2000 > 20) continue;
        if (e50 - e2000 < 3) continue;
        return { dispo, c50, c2000, f50, f2000, e50, e2000 };
    }
    // Repli déterministe : une petite série qui s'écarte, une grande qui colle.
    const d0 = DISPOSITIFS_SIM[1];
    return { dispo: d0, c50: 30, c2000: 1002, f50: 60, f2000: 50, e50: 10, e2000: 0 };
};

const introSimu = (s) => `<p>On répète toujours la même épreuve : on lance ${s.dispo.nom} et on note le `
    + `résultat. On compte combien de fois on observe « ${s.dispo.evt} ». `
    + `Le modèle donne à cet évènement ${s.dispo.chances}.</p>`
    + `<p>Première série : ${s.c50} fois sur ${PETITE_SERIE} répétitions. `
    + `Seconde série : ${s.c2000} fois sur ${GRANDE_SERIE} répétitions.</p>`;

const paramsSimu = (s, extra = {}) => ({
    niveau: 3,
    type_de_situation: 'situation non symétrique observable',
    nombre_d_issues_de_l_experience: s.dispo.issues,
    nombre_d_epreuves: 1,
    nombre_de_repetitions_simulees: [PETITE_SERIE, GRANDE_SERIE],
    effectif_observe_d_une_issue: [s.c50, s.c2000],
    frequence_observee_d_une_issue: [s.f50 / 100, s.f2000 / 100],
    ecart_entre_la_frequence_observee_et_la_probabilite_du_modele: [s.e50 / 100, s.e2000 / 100],
    nombre_de_questions_par_situation: 2,
    ...extra,
});

const genSimuTrous = () => {
    const s = tirerSimulation();
    const phrase = `Fréquence observée sur ${PETITE_SERIE} répétitions : {{${centiemesFr(s.f50)}}}\n`
        + `Fréquence observée sur ${GRANDE_SERIE} répétitions : {{${centiemesFr(s.f2000)}}}`;
    const payload = makeTexteTrou(phrase,
        `${introSimu(s)}<p>Calcule la fréquence observée dans chaque série, ARRONDIE AU CENTIÈME `
        + '(par exemple 0,17).</p>',
        { ...opts(L_SIMU), mode: 'saisie', strict: false });
    payload._meta.params = paramsSimu(s, { erreurVisee: 'frequence_egale_a_la_probabilite', nombre_de_trous: 2 });
    return payload;
};

const genSimuQcm = () => {
    const s = tirerSimulation();
    const bonne = `La série de ${GRANDE_SERIE} répétitions : plus une série est longue, plus la fréquence observée se rapproche de la probabilité du modèle`;
    const dist = [
        { code: 'frequence_egale_a_la_probabilite', valeur: `Aucune des deux : la fréquence devrait valoir exactement ${centiemesFr(s.dispo.pCent)}, donc le dispositif est truqué` },
        { code: 'raisonnement_de_compensation', valeur: `La série de ${PETITE_SERIE} répétitions : après plusieurs échecs, le résultat attendu devient plus probable et se rattrape plus vite` },
        { code: 'situation_confondue_avec_le_modele', valeur: 'Aucune des deux : une fréquence observée n\'a rien à voir avec la probabilité du modèle' },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `${introSimu(s)}<p>Les fréquences observées valent ${centiemesFr(s.f50)} puis ${centiemesFr(s.f2000)}. `
        + 'Laquelle des deux séries renseigne le mieux sur le modèle, et pourquoi ?</p>',
        opts(L_SIMU));
    payload._meta.params = paramsSimu(s, { distracteurs: dist });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « La situation et son modèle » (niveau 4)
// ═════════════════════════════════════════════════════════════

const SANS_HASARD = [
    "On regarde quel jour de la semaine vient après le lundi.",
    "On calcule la somme 12 + 19 et on regarde le résultat obtenu.",
    "On mesure la longueur d'un segment déjà tracé sur une feuille, avec la même règle.",
    "On ouvre un livre à la page 40 et on regarde le numéro de cette page.",
    "On compte le nombre de côtés d'un carré dessiné au tableau.",
    "On lit la température affichée par un thermomètre posé depuis une heure dans la salle.",
    "On regarde la lettre qui suit le M dans l'alphabet.",
    "On divise 144 par 12 et on note le quotient.",
    "On regarde le mois qui vient juste après le mois de mars.",
    "On compte le nombre de faces d'un dé cubique posé sur la table.",
    "On relit l'heure affichée par une horloge arrêtée depuis hier.",
    "On regarde le résultat de 7 multiplié par 8 dans une table de multiplication.",
];

const AVEC_HASARD = [
    { desc: "On lance une pièce équilibrée et on note le côté obtenu.", evt: "PILE", chances: "une chance sur deux", issues: 2 },
    { desc: "On lance une pièce équilibrée et on note le côté obtenu.", evt: "FACE", chances: "une chance sur deux", issues: 2 },
    { desc: "On lance un dé équilibré à six faces et on note le numéro obtenu.", evt: "le 6", chances: "une chance sur six", issues: 6 },
    { desc: "On lance un dé équilibré à six faces et on note le numéro obtenu.", evt: "le 1", chances: "une chance sur six", issues: 6 },
    { desc: "On lance un dé équilibré à six faces et on note le numéro obtenu.", evt: "le 3", chances: "une chance sur six", issues: 6 },
    { desc: "On tire une boule au hasard dans une urne de dix boules indiscernables numérotées de 1 à 10.", evt: "la boule 7", chances: "une chance sur dix", issues: 10 },
    { desc: "On tire une boule au hasard dans une urne de dix boules indiscernables numérotées de 1 à 10.", evt: "la boule 3", chances: "une chance sur dix", issues: 10 },
    { desc: "On fait tourner une roue à huit secteurs égaux numérotés de 1 à 8 et on note le secteur désigné.", evt: "le secteur 5", chances: "une chance sur huit", issues: 8 },
    { desc: "On fait tourner une roue à huit secteurs égaux numérotés de 1 à 8 et on note le secteur désigné.", evt: "le secteur 2", chances: "une chance sur huit", issues: 8 },
    { desc: "On tire un jeton au hasard parmi cinq jetons identiques numérotés de 1 à 5.", evt: "le jeton 4", chances: "une chance sur cinq", issues: 5 },
    { desc: "On tire un jeton au hasard parmi cinq jetons identiques numérotés de 1 à 5.", evt: "le jeton 1", chances: "une chance sur cinq", issues: 5 },
    { desc: "On tire une carte au hasard dans un paquet de quatre cartes indiscernables numérotées de 1 à 4.", evt: "la carte 2", chances: "une chance sur quatre", issues: 4 },
];

const paramsModele = (type, extra = {}) => ({
    niveau: 4,
    type_de_situation: type,
    nombre_d_epreuves: 1,
    nombre_de_questions_par_situation: 2,
    ...extra,
});

const genModeleQcm = () => {
    if (randInt(0, 1) === 0) {
        const a = randomChoice(AVEC_HASARD);
        const bonne = `Le modèle attribue ${a.chances} à l'évènement « ${a.evt} ».`;
        const dist = [
            { code: 'situation_confondue_avec_le_modele', valeur: `Dans la réalité, on obtient « ${a.evt} » exactement ${a.chances.replace('une chance sur', 'une fois sur')}.` },
            { code: 'situation_confondue_avec_le_modele', valeur: `Si on répète l'épreuve, « ${a.evt} » sort régulièrement, à intervalle fixe.` },
            { code: 'raisonnement_de_compensation', valeur: `Quand on n'a pas obtenu « ${a.evt} » depuis longtemps, cet évènement devient plus probable.` },
        ];
        const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
            `<p>${a.desc}</p><p>Quelle affirmation est correctement formulée ? Attention à ne pas confondre `
            + 'ce que fait la situation réelle et ce que dit le modèle.</p>',
            opts(L_MODELE));
        payload._meta.params = paramsModele('situation symétrique', {
            nombre_d_issues_de_l_experience: a.issues,
            distracteurs: dist,
        });
        return payload;
    }
    const s = randomChoice(SANS_HASARD);
    const bonne = 'Non : le résultat est connu d\'avance, il n\'y a rien à modéliser par le hasard';
    const dist = [
        { code: 'hasard_implique_equiprobabilite', valeur: 'Oui : tant qu\'on n\'a pas regardé le résultat, il est au hasard' },
        { code: 'hasard_implique_equiprobabilite', valeur: 'Oui, et toutes les issues possibles ont alors la même probabilité' },
        { code: 'situation_confondue_avec_le_modele', valeur: 'Oui : toute situation que l\'on n\'a pas encore observée est une expérience aléatoire' },
    ];
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `<p>${s}</p><p>Est-ce une expérience aléatoire ?</p>`,
        opts(L_MODELE));
    payload._meta.params = paramsModele('situation où le hasard n\'intervient pas', { distracteurs: dist });
    return payload;
};

const genModeleVF = () => {
    const a = randomChoice(AVEC_HASARD);
    const s = randomChoice(SANS_HASARD);
    const vs = equilibrer([0, 1, 2, 3].map(() => randInt(0, 1) === 0));
    const codes = [];
    if (!vs[0]) codes.push('situation_confondue_avec_le_modele');
    if (!vs[1]) codes.push('raisonnement_de_compensation');
    if (!vs[2]) codes.push('hasard_implique_equiprobabilite');
    if (!vs[3]) codes.push('frequence_egale_a_la_probabilite');
    const lignes = [
        [vs[0]
            ? `Le modèle attribue ${a.chances} à l'évènement « ${a.evt} ».`
            : `Dans la réalité, « ${a.evt} » sort une fois sur ${a.chances.replace('une chance sur ', '')}, sans exception.`, vs[0] ? 0 : 1],
        [vs[1]
            ? 'Une épreuve répétée ne garde aucune mémoire des résultats précédents.'
            : `Après une longue série sans « ${a.evt} », cet évènement devient plus probable.`, vs[1] ? 0 : 1],
        [vs[2]
            ? `« ${s} » n'est pas une expérience aléatoire : le résultat est connu d'avance.`
            : `« ${s} » est une expérience aléatoire dont toutes les issues ont la même probabilité.`, vs[2] ? 0 : 1],
        [vs[3]
            ? 'Une fréquence observée s\'approche de la probabilité du modèle sans lui être exactement égale.'
            : 'Une fréquence observée doit être exactement égale à la probabilité du modèle.', vs[3] ? 0 : 1],
    ];
    const melange = shuffle(lignes);
    const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
        `<p>${a.desc}</p><p>Vrai ou faux ?</p>`, opts(L_MODELE));
    payload._meta.params = paramsModele('situation symétrique', {
        nombre_d_issues_de_l_experience: a.issues,
        distracteurs: codes.map((code) => ({ code })),
    });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « L'échelle de probabilité » (niveau 1 — automatisme)
// ═════════════════════════════════════════════════════════════

/** Tous les événements portent sur UNE SEULE épreuve (exclusion « aucune
 *  expérience à deux épreuves ») et n'emploient ni « et », ni « ou », ni
 *  évènement contraire (exclusion des réunions et intersections). */
const POSITIONS = [
    {
        id: 'zero', cible: '0 (impossible)',
        evts: ['obtenir 7 en lançant un dé dont les faces vont de 1 à 6',
            'tirer une bille rouge dans un sac ne contenant que des billes noires',
            'obtenir 0 en lançant un dé dont les faces vont de 1 à 6'],
    },
    {
        id: 'quasi0', cible: 'très proche de 0 (mais pas impossible)',
        evts: ['gagner le gros lot du loto avec une seule grille',
            'tirer la seule bille rouge d\'un sac qui contient mille billes',
            'tirer le seul jeton gagnant d\'un sac de dix mille jetons'],
    },
    {
        id: 'sixieme', cible: '1/6',
        evts: ['obtenir 3 en lançant un dé équilibré',
            'obtenir 6 en lançant un dé équilibré',
            'obtenir 1 en lançant un dé équilibré'],
    },
    {
        id: 'demi', cible: '1/2',
        evts: ['obtenir PILE en lançant une pièce équilibrée',
            'obtenir un nombre pair en lançant un dé équilibré',
            'tirer une bille rouge dans un sac contenant 5 billes rouges et 5 billes noires'],
    },
    {
        id: 'quasi1', cible: 'très proche de 1 (mais pas certain)',
        evts: ['tirer une bille noire dans un sac contenant 999 billes noires et 1 bille rouge',
            'tirer un jeton perdant dans un sac de mille jetons dont un seul est gagnant'],
    },
    {
        id: 'un', cible: '1 (certain)',
        evts: ['obtenir un nombre entre 1 et 6 en lançant un dé classique',
            'tirer une bille noire dans un sac ne contenant que des billes noires'],
    },
];

const elEchelle = () => imgElement(svgEchelle(), 'compact');

const paramsEchelle = (extra = {}) => ({
    niveau: 1,
    type_de_situation: 'situation symétrique',
    nombre_d_epreuves: 1,
    ...extra,
});

const genEchelleRelier = () => {
    const positions = shuffle([...POSITIONS]).slice(0, 4);
    const pairs = positions.map((p) => ({ source: cap(randomChoice(p.evts)), target: fr(p.cible) }));
    const payload = makeRelier(pairs,
        '<p>Sur une échelle de probabilité, de 0 (évènement impossible) à 1 (évènement certain), '
        + 'relie chaque évènement à la position de sa probabilité.</p>',
        opts(L_ECHELLE));
    payload.elements_additionnels = elEchelle();
    payload._meta.params = paramsEchelle();
    return payload;
};

const genEchelleClasser = () => {
    const de = (id, n) => {
        const p = POSITIONS.find((x) => x.id === id);
        return shuffle([...p.evts]).slice(0, n).map(cap);
    };
    const payload = makeClasser([
        { name: 'Probabilité nulle ou très proche de 0', items: [...de('zero', 1), ...de('quasi0', 1)] },
        { name: 'Probabilité proche de $\\frac{1}{2}$', items: de('demi', 2) },
        { name: 'Probabilité égale ou très proche de 1', items: [...de('un', 1), ...de('quasi1', 1)] },
    ],
    '<p>Classe chaque évènement selon la position de sa probabilité sur l\'échelle de 0 à 1.</p>',
    opts(L_ECHELLE));
    payload.elements_additionnels = elEchelle();
    payload._meta.params = paramsEchelle();
    return payload;
};

const genEchelleQcm = () => {
    const positions = shuffle([...POSITIONS]).slice(0, 4);
    const p = positions[0];
    const evt = randomChoice(p.evts);
    const payload = makeQcm(shuffle(positions.map((x) => fr(x.cible))), fr(p.cible),
        '<p>Sur l\'échelle de probabilité, de 0 (impossible) à 1 (certain), où se situe la probabilité '
        + `de l'évènement : « ${evt} » ?</p>`,
        opts(L_ECHELLE));
    payload.elements_additionnels = elEchelle();
    payload._meta.params = paramsEchelle();
    return payload;
};

const genEchelleDrag = () => {
    const evtDe = (id) => randomChoice(POSITIONS.find((p) => p.id === id).evts);
    const segments = shuffle([
        randInt(0, 1) === 0
            ? 'un évènement impossible a une probabilité de {{0}}'
            : `« ${evtDe('zero')} » : {{0}}`,
        `« ${evtDe('sixieme')} » : {{1/6}}`,
        `« ${evtDe('demi')} » : {{1/2}}`,
        randInt(0, 1) === 0
            ? 'un évènement certain a une probabilité de {{1}}'
            : `« ${evtDe('un')} » : {{1}}`,
    ]);
    const phrase = segments.map(cap).join('\n');
    const distracteurs = filtrerDistracteurs(phrase, ['1/4', '2', '0,9']);
    const payload = makeTexteTrou(phrase,
        '<p>Place chaque étiquette de probabilité au bon endroit.</p>',
        { ...opts(L_ECHELLE), mode: 'drag', distracteurs });
    payload._meta.params = paramsEchelle({ nombre_de_trous: 4 });
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    urne_qcm_unique: genUrneQcm,
    urne_tableau_vf: genUrneVF,
    de_ordonner: genDeOrdonner,
    de_qcm_unique: genDeQcm,
    de_tableau_vf: genDeVF,
    billes_qcm_unique: genBillesQcm,
    billes_texte_trou_saisie: genBillesTrous,
    billes_tableau_vf: genBillesVF,
    simu_texte_trou_saisie: genSimuTrous,
    simu_qcm_unique: genSimuQcm,
    modele_qcm_unique: genModeleQcm,
    modele_tableau_vf: genModeleVF,
    echelle_relier: genEchelleRelier,
    echelle_classer: genEchelleClasser,
    echelle_qcm_unique: genEchelleQcm,
    echelle_texte_trou_drag: genEchelleDrag,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', ordonner: 'Ordonner', classer: 'Classer', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'urne', label: L_URNE, formatsList: buildFormats('urne') },
    { id: 'de', label: L_DE, formatsList: buildFormats('de') },
    { id: 'billes', label: L_BILLES, formatsList: buildFormats('billes') },
    { id: 'simu', label: L_SIMU, formatsList: buildFormats('simu') },
    { id: 'modele', label: L_MODELE, formatsList: buildFormats('modele') },
    { id: 'echelle', label: L_ECHELLE, formatsList: buildFormats('echelle') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
